import React, { useState, useEffect } from "react";
import { Container } from "./styles";
import { Button, Form, Input, Select, Spin, Upload } from "antd";
import { toast } from "react-toastify";
import ContentBlockEditor from "../../components/ContentBlockEditor";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useLanguage } from "../../hooks/LanguageContext";
import api from "../../services/api";
import { returnCatch } from "../../services/functions";

const { Option } = Select;

export default function ExerciseForm() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [error, setError] = useState(false);
  const [contents, setContents] = useState([]);
  const [photo, setPhoto] = useState("");
  const [contentsDelete, setContentsDelete] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const { language } = useLanguage();
  const isEditing = !!id;

  useEffect(() => {
    if (id) {
      loadingScreen(id);
    } else {
      setLoading(false);
    }
  }, [id]);

  async function loadingScreen() {
    setLoading(true);
    await api
      .get(`/admin/exercise/${id}`)
      .then((response) => {
        form.setFieldsValue({
          title: response.data.title,
          description: response.data.description,
          body_part: response.data.body_part,
          intensity: response.data.intensity,
          phase: response.data.phase,
          duration: response.data.duration,
        });
        setContents(response.data.contents);
        setPhoto({ photo_url: response.data.photo_url });
      })
      .catch(({ response }) => {
        returnCatch(response);
      });
    setLoading(false);
  }

  async function handleSubmit(values) {
    if (!photo || contents.some((item) => !item.value) || !contents.length) {
      toast.warn("Preencha todos os campos e envie uma imagem de capa");
      setError(true);
    } else {
      setLoadingButton(true);
      if (isEditing) {
        await editedExercise(values);
      } else {
        await createdExercise(values);
      }
      setLoadingButton(false);
    }
  }

  async function createdExercise(values) {
    const data = new FormData();

    const contentsFile = contents.filter((item) => {
      return item.type == "IMAGE" || item.type == "VIDEO";
    });
    const contentsText = contents.filter((item) => {
      return item.type != "IMAGE" && item.type != "VIDEO";
    });

    data.append("title", values.title);
    data.append("description", values.description);
    data.append("body_part", values.body_part);
    data.append("intensity", values.intensity);
    data.append("phase", values.phase);
    data.append("duration", values.duration);
    data.append("language", language);
    data.append("contents", JSON.stringify(contentsText));
    if (photo.name) {
      data.append("file", photo, photo.name);
    }

    await api
      .post("/admin/exercise", data)
      .then(async (response) => {
        let error = "";
        Promise.all(
          await contentsFile.map(async (item) => {
            const data = new FormData();
            console.log(item);
            data.append("order", item.order);
            data.append("type", item.type);
            data.append("exercise_id", id);
            if (item.value.name) {
              data.append("file", item.value, item.value.name);
            }
            await api
              .post(`/admin/content-exercise`, data)
              .then(() => {})
              .catch(({ response }) => {
                error = error
                  ? `, posição ${item.idx}`
                  : `, mas não foi possivel criar o conteudo da posição ${item.idx}`;
              });
          }),
        );
        toast({
          type: error ? "warning" : "success",
          message: "Exercicio criado com sucesso",
        });
        navigate(`/exercises/edit/${response.data.id}`);
      })
      .catch(({ response }) => {
        returnCatch(response);
      });
  }

  async function editedExercise(values) {
    const data = new FormData();

    const contentsFile = contents.filter((item) => {
      return item.type == "IMAGE" || item.type == "VIDEO";
    });
    const contentsText = contents.filter((item) => {
      return item.type != "IMAGE" && item.type != "VIDEO";
    });

    data.append("title", values.title);
    data.append("description", values.description);
    data.append("body_part", values.body_part);
    data.append("intensity", values.intensity);
    data.append("phase", values.phase);
    data.append("duration", values.duration);
    data.append("language", language);
    data.append("contents", JSON.stringify(contentsText));
    data.append("contentsDelete", JSON.stringify(contentsDelete));
    if (photo.name) {
      data.append("file", photo, photo.name);
    }

    await api
      .put(`/admin/exercise/${id}`, data)
      .then(async (response) => {
        let error = "";
        await Promise.all(
          await contentsFile.map(async (item) => {
            const data = new FormData();

            data.append("order", item.order);
            data.append("type", item.type);
            if (item.value.name) {
              data.append("file", item.value, item.value.name);
            }

            if (item.id) {
              await api
                .put(`/admin/content-exercise/${item.id}`, data)
                .then(() => {})
                .catch(({ response }) => {
                  error = error
                    ? `${error}, ${item.order + 1}`
                    : `, mas não foi possivel criar/editar o conteudo da posição ${item.order + 1}`;
                });
            } else {
              data.append("exercise_id", id);
              await api
                .post(`/admin/content-exercise`, data)
                .then(() => {})
                .catch(({ response }) => {
                  error = error
                    ? `${error}, ${item.order + 1}`
                    : `, mas não foi possivel criar/editar o conteudo da posição ${item.order + 1}`;
                });
            }
          }),
        );
        if (error) {
          toast.warning("Exercicio editado com sucesso" + error);
        } else {
          toast.success("Exercicio editado com sucesso");
        }

        navigate(`/exercises/edit/${response.data.id}`);
      })
      .catch(({ response }) => {
        returnCatch(response);
      });
  }

  return (
    <Container>
      <div style={{ maxWidth: 800, width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 15,
            marginBottom: 20,
          }}
        >
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/exercises")}
            shape="circle"
          />
          <h1 style={{ margin: 0 }}>
            {isEditing ? "Editar Exercicio" : "Novo Exercicio"}
          </h1>
        </div>
        {loading ? (
          <Spin />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{
              background: "#fff",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px 20px",
              }}
            >
              <Form.Item
                label="Imagem de Capa"
                style={{
                  width: "100%",
                  marginBottom: 0,
                }}
                required
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {photo && (
                    <img
                      draggable={false}
                      src={photo.photo_url}
                      alt="avatar"
                      style={{
                        width: 150,
                        borderRadius: 10,
                        height: 150,
                        marginRight: 15,
                      }}
                    />
                  )}
                  <Upload
                    name="avatar"
                    listType="picture-card"
                    className="avatar-uploader"
                    showUploadList={false}
                    maxCount={1}
                    onRemove={() => {
                      setPhoto(null);
                    }}
                    beforeUpload={(currentFile) => {
                      const isImage = currentFile.type.startsWith("image/");
                      if (!isImage) {
                        toast.error(
                          "Você só pode carregar arquivos de imagem!",
                        );
                        return Upload.LIST_IGNORE;
                      }

                      currentFile.photo_url = URL.createObjectURL(currentFile);
                      setPhoto(currentFile);

                      console.log(currentFile);
                      return false;
                    }}
                    fileList={photo ? [photo] : []}
                  >
                    <div
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>
                        {photo ? "Editar" : "Adicionar"}
                      </div>
                    </div>
                  </Upload>
                </div>
              </Form.Item>
              <Form.Item
                name="title"
                label="Título"
                rules={[{ required: true, message: "Insira o título da capa" }]}
                style={{
                  marginBottom: 0,
                  width: "100%",
                }}
              >
                <Input
                  placeholder="Ex: Alongamento"
                  size="large"
                  style={{
                    width: "100%",
                  }}
                />
              </Form.Item>
              <Form.Item
                name="description"
                label="Descrição"
                rules={[{ required: true, message: "Insira a descrição" }]}
                style={{
                  marginBottom: 0,
                  alignSelf: "center",
                  width: "100%",
                }}
              >
                <Input.TextArea
                  placeholder="Ex: Nesse exercicio você..."
                  size="large"
                  style={{ width: "100%", minHeight: 80 }}
                />
              </Form.Item>
              <Form.Item
                name="body_part"
                label="Área de Foco"
                rules={[
                  { required: true, message: "Selecione a área de foco" },
                ]}
                style={{
                  marginBottom: 0,
                  flex: 1,
                }}
              >
                <Input
                  placeholder="Ex: Corpo todo"
                  size="large"
                  style={{
                    width: "100%",
                  }}
                />
              </Form.Item>
              <Form.Item
                name="phase"
                label="Fase do ciclo"
                rules={[{ required: true, message: "Insira a fase do ciclo" }]}
                style={{
                  marginBottom: 0,
                  flex: 1,
                }}
              >
                <Select
                  placeholder="Ex: Menstrual"
                  size="large"
                  style={{ minWidth: 300 }}
                >
                  <Option value="MENSTRUAL">Menstrual</Option>
                  <Option value="FOLICULAR">Folicular</Option>
                  <Option value="OVULATORIA">Ovulátoria</Option>
                  <Option value="LUTEA">Lutea</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="intensity"
                label="Intensidade"
                rules={[{ required: true, message: "Insira a intensidade" }]}
                style={{
                  marginBottom: 0,
                  flex: 1,
                }}
              >
                <Input
                  placeholder="Ex: Alta"
                  size="large"
                  style={{ minWidth: 300 }}
                />
              </Form.Item>
              <Form.Item
                name="duration"
                label="Duração"
                rules={[{ required: true, message: "Insira o duração" }]}
                style={{
                  marginBottom: 0,
                  flex: 1,
                }}
              >
                <Input
                  placeholder="Ex: 20min"
                  size="large"
                  style={{ minWidth: 300 }}
                />
              </Form.Item>
            </div>

            <ContentBlockEditor
              blocks={contents}
              error={error}
              setBlocks={setContents}
              removeBlock={(id, index) => {
                setContents(contents.filter((block, idx) => index !== idx));
                if (id) {
                  setContentsDelete([...contentsDelete, id]);
                }
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 40,
                gap: 15,
              }}
            >
              <Button size="large" onClick={() => navigate("/exercises")}>
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loadingButton}
                size="large"
              >
                {isEditing ? "Salvar Alterações" : "Criar Exercicio"}
              </Button>
            </div>
          </Form>
        )}
      </div>
    </Container>
  );
}
